import React, { useMemo } from 'react';
import './SprintGantt.css';

function parseData(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
function diaUTC(d) { return Math.floor(d.getTime() / 86400000); }
function fmt(d) {
  return d ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—';
}

// Gantt de sprints (Fase 9B) — barras posicionadas numa escala de tempo.
// Sem dependência externa; usa posicionamento percentual.
function SprintGantt({ sprints, detalhes, chamadosPorSprint, onAbrir }) {
  const linhas = useMemo(() => {
    const arr = (sprints || []).map((s) => {
      const det = (detalhes && detalhes[s.id]) || s;
      const ini = parseData(det.data_inicio || s.data_inicio);
      const fim = parseData(det.data_fim || s.data_fim);
      const chamados = (chamadosPorSprint && chamadosPorSprint[s.id]) || [];
      const total = chamados.length;
      const concluidos = chamados.filter((c) => String(c.status || '').toLowerCase() === 'concluido').length;
      const ativo = s.ativo === 1 || s.ativo === true;
      const fechada = !!(det.concluida_em || s.concluida_em);
      return {
        id: s.id,
        nome: det.nome || s.nome || 'Sprint',
        responsavel: det.responsavel_nome || '',
        ini, fim, total, concluidos,
        estado: fechada ? 'fechada' : ativo ? 'ativa' : 'planejada',
      };
    }).filter((x) => x.ini && x.fim && x.fim >= x.ini);
    return arr.sort((a, b) => a.ini - b.ini);
  }, [sprints, detalhes, chamadosPorSprint]);

  const escala = useMemo(() => {
    if (linhas.length === 0) return null;
    let min = linhas[0].ini; let max = linhas[0].fim;
    for (const l of linhas) { if (l.ini < min) min = l.ini; if (l.fim > max) max = l.fim; }
    // margem de alguns dias para respiro
    const minD = diaUTC(min) - 1;
    const maxD = diaUTC(max) + 1;
    const span = Math.max(1, maxD - minD);
    // marcas de mês
    const marcas = [];
    const cursor = new Date(min.getFullYear(), min.getMonth(), 1);
    const fimDate = new Date(max.getFullYear(), max.getMonth() + 1, 1);
    while (cursor <= fimDate) {
      const pos = ((diaUTC(cursor) - minD) / span) * 100;
      if (pos >= 0 && pos <= 100) {
        marcas.push({ pos, label: cursor.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }) });
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }
    const hojePos = ((diaUTC(new Date()) - minD) / span) * 100;
    return { minD, span, marcas, hojePos };
  }, [linhas]);

  if (linhas.length === 0) {
    return <div className="alert alert-info py-3">Nenhuma sprint com datas válidas para a linha do tempo.</div>;
  }

  const posPct = (d) => ((diaUTC(d) - escala.minD) / escala.span) * 100;

  return (
    <div className="sprint-gantt">
      <div className="sprint-gantt-scroll">
        <div className="sprint-gantt-inner">
          {/* régua de meses */}
          <div className="sprint-gantt-regua">
            {escala.marcas.map((m, i) => (
              <span key={i} className="sprint-gantt-marca" style={{ left: `${m.pos}%` }}>{m.label}</span>
            ))}
          </div>

          <div className="sprint-gantt-linhas">
            {/* linha do "hoje" — overlay alinhado à trilha (após o rótulo de 220px) */}
            {escala.hojePos >= 0 && escala.hojePos <= 100 && (
              <div className="sprint-gantt-hoje-overlay">
                <div className="sprint-gantt-hoje" style={{ left: `${escala.hojePos}%` }} title="Hoje" />
              </div>
            )}
            {linhas.map((l) => {
              const left = posPct(l.ini);
              const width = Math.max(1.5, posPct(l.fim) - left);
              const progresso = l.total > 0 ? Math.round((l.concluidos / l.total) * 100) : null;
              return (
                <div key={l.id} className="sprint-gantt-row">
                  <div className="sprint-gantt-rotulo" title={l.nome}>
                    <span className="sprint-gantt-rotulo-nome">{l.nome}</span>
                    {l.responsavel && <span className="sprint-gantt-rotulo-resp">{l.responsavel}</span>}
                  </div>
                  <div className="sprint-gantt-trilha">
                    <button
                      type="button"
                      className={`sprint-gantt-barra estado-${l.estado}`}
                      style={{ left: `${left}%`, width: `${width}%` }}
                      onClick={() => onAbrir && onAbrir(l.id)}
                      title={`${l.nome} · ${fmt(l.ini)} a ${fmt(l.fim)}${progresso != null ? ` · ${progresso}% concluído` : ''}`}
                    >
                      {progresso != null && <span className="sprint-gantt-progresso" style={{ width: `${progresso}%` }} />}
                      <span className="sprint-gantt-barra-texto">{fmt(l.ini)} – {fmt(l.fim)}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sprint-gantt-legenda">
        <span><i className="sprint-gantt-dot estado-ativa" /> Ativa</span>
        <span><i className="sprint-gantt-dot estado-planejada" /> Não iniciada</span>
        <span><i className="sprint-gantt-dot estado-fechada" /> Fechada</span>
      </div>
    </div>
  );
}

export default SprintGantt;
